import { z } from 'zod/v4';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@shared/api';
import { createQuery } from '@shared/lib/query-helpers';

export const shopifyWarehouseMappingKeys = {
  locations: (scope: string) => ['shopify-locations', scope] as const,
  mappings: (scope: string) => ['shopify-warehouse-mappings', scope] as const,
  options: (scope: string) => ['shopify-warehouse-options', scope] as const,
};

export const shopifyLocationsSchema = z.object({
  data: z.object({
    items: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          isActive: z.boolean(),
          isServiceManaged: z.boolean(),
          eligibleForMapping: z.boolean(),
        })
      )
      .max(1000),
  }),
  meta: z.object({
    storeDomain: z.string(),
    limit: z.literal(1000),
    total: z.number().int().nonnegative(),
    complete: z.literal(true),
  }),
});
export const shopifyMappingsSchema = z.object({
  data: z.object({
    storeDomain: z.string(),
    environment: z.string(),
    revision: z.number().int().nonnegative(),
    mappings: z
      .array(
        z.object({
          locationId: z.string(),
          warehouseSourceRecordId: z.string(),
          warehouseContractId: z.uuid(),
        })
      )
      .max(1000),
    updatedAt: z.iso.datetime().nullable(),
    canSave: z.boolean(),
    availability: z.enum(['available', 'not-configured']),
    saveDisabledReason: z.string().nullable(),
  }),
});
export type ShopifyLocation = z.infer<typeof shopifyLocationsSchema>['data']['items'][number];
export type ShopifyWarehouseMapping = z.infer<
  typeof shopifyMappingsSchema
>['data']['mappings'][number];
export const shopifyWarehouseOptionsSchema = z.object({
  data: z.object({
    items: z
      .array(
        z.object({
          id: z.string(),
          name: z.string().nullable(),
          code: z.string().nullable(),
          isPrimary: z.boolean().nullable(),
          isStore: z.boolean().nullable(),
          isMobileWarehouse: z.boolean().nullable(),
          isDisabled: z.boolean().nullable(),
          cancelled: z.boolean().nullable(),
        })
      )
      .max(1000),
  }),
  meta: z.object({ warehouseContractId: z.uuid(), environment: z.string() }),
});
export type ShopifyWarehouseOption = z.infer<
  typeof shopifyWarehouseOptionsSchema
>['data']['items'][number];

export function shopifyWarehouseOptionsQuery(scope: string) {
  return createQuery({
    path: '/api/v1/shopify/warehouse-options',
    queryKey: shopifyWarehouseMappingKeys.options(scope),
    schema: shopifyWarehouseOptionsSchema,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    consumeAbortSignal: true,
    refetchOnWindowFocus: false,
  });
}

export function shopifyLocationsQuery(scope: string) {
  return createQuery({
    path: '/api/v1/shopify/locations',
    queryKey: shopifyWarehouseMappingKeys.locations(scope),
    schema: shopifyLocationsSchema,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    consumeAbortSignal: true,
    refetchOnWindowFocus: false,
  });
}
export function shopifyMappingsQuery(scope: string) {
  return queryOptions({
    queryKey: shopifyWarehouseMappingKeys.mappings(scope),
    queryFn: async ({ signal }) =>
      versionedMappings(
        await api.getWithMetadata('/api/v1/shopify/warehouse-mappings', {
          signal,
          schema: shopifyMappingsSchema,
        })
      ),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

function versionedMappings(response: {
  data: z.infer<typeof shopifyMappingsSchema>;
  headers: Headers;
}) {
  const etag = z
    .string()
    .regex(/^"[^"\r\n]+"$/u)
    .parse(response.headers.get('etag'));
  return { ...response.data, etag };
}

export type ShopifyMappingVersion = z.infer<typeof shopifyMappingsSchema> & { etag: string };

const saveMappingsSchema = z
  .object({
    mappings: z
      .array(
        z
          .object({
            locationId: z
              .string()
              .regex(/^gid:\/\/shopify\/Location\/[0-9]+$/u)
              .max(100),
            warehouseSourceRecordId: z
              .string()
              .regex(/^(0|[1-9][0-9]*)$/u)
              .max(30),
            warehouseContractId: z.uuid(),
          })
          .strict()
      )
      .max(1000),
  })
  .strict();

export async function saveShopifyMappings(
  mappings: ShopifyWarehouseMapping[],
  etag: string
): Promise<ShopifyMappingVersion> {
  const body = saveMappingsSchema.parse({ mappings });
  const version = z
    .string()
    .regex(/^"[^"\r\n]+"$/u)
    .parse(etag);
  return versionedMappings(
    await api.putWithMetadata('/api/v1/shopify/warehouse-mappings', body, {
      headers: { 'If-Match': version },
      schema: shopifyMappingsSchema,
    })
  );
}
